import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Card, ScoreDisplay, PlayerAvatar, Ico, Icons, StatusBadge, resolveTeam, PlayersListSkeleton, PlayerDetailSkeleton } from '../../components/common';
import { playerAPI, notificationAPI } from '../../services/api';
import { formatDateIST } from '../../utils/dateUtils';

export function PlayersList() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('points');
  const [ready, setReady] = useState(false);
  const [players, setPlayers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const limit = 30;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const loadPlayers = async (pageNum = 1, searchQuery = '', sortField = 'points', append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: limit,
        sort: sortField === 'wins' ? '-wins' : sortField === 'titles' ? '-titles' : '-points',
        ...(searchQuery && { search: searchQuery })
      });
      
      const res = await playerAPI.getAll(params.toString());
      
      if (append) {
        setPlayers(prev => [...prev, ...(res.data || [])]);
      } else {
        setPlayers(res.data || []);
      }
      
      setTotal(res.total || 0);
      setHasMore((res.data || []).length === limit && (pageNum * limit) < (res.total || 0));
      setReady(true);
    } catch (err) {
      console.error(err);
      setReady(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPlayers(1, debouncedSearch, sortBy, false);
    setPage(1);
  }, [debouncedSearch, sortBy]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadPlayers(nextPage, search, sortBy, true);
  };

  if (!ready) return <PlayersListSkeleton />;

  return (
    <div className="container-sm">
      <h1 className="heading" style={{ marginBottom: 16 }}>PLAYERS</h1>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140, position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input 
            className="form-input" 
            style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }} 
            placeholder="Search players..." 
            value={search} 
            onChange={e => setSearch(e.target.value)}
            onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
            onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
        <select className="form-input" style={{ width: 'auto' }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="points">By Points</option><option value="wins">By Wins</option><option value="titles">By Titles</option>
        </select>
      </div>
      <div className="grid-players">
        {players.length === 0 && (
          <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#B0B8C9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                <path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
            </div>
            <h3 className="empty-title">No Players Found</h3>
            <p className="empty-sub">{search ? 'Try a different search' : 'No players registered yet'}</p>
          </div>
        )}
        {players.map(p => {
          const wr = p.matchesPlayed ? ((p.wins / p.matchesPlayed) * 100).toFixed(0) : 0;
          return (
            <Link to={`/players/${p.id}`} key={p.id} style={{ textDecoration: 'none' }}>
              <div className="p-card">
                <div className="p-top">
                  <PlayerAvatar name={p.name} photo={p.photo} size={34} />
                  <div style={{ flex: 1 }}>
                    <div className="p-name">{p.name}</div>
                    <div className="p-country">{p.country}</div>
                  </div>
                </div>
                <div className="p-stats">
                  <span style={{ color: '#43A047' }}>{p.wins}W</span>
                  <span className="p-dot">·</span>
                  <span style={{ color: '#E53935' }}>{p.losses}L</span>
                  <span className="p-dot">·</span>
                  <span style={{ color: '#FB8C00' }}>{p.titles}T</span>
                  {(p.points || 0) > 0 && <><span className="p-dot">·</span><span style={{ color: '#1E88E5', fontWeight: 700 }}>{p.points}pts</span></>}
                </div>
                <div className="bar bar-thin"><div className="bar-fill" style={{ width: `${wr}%`, background: '#00205B' }} /></div>

              </div>
            </Link>
          );
        })}
      </div>
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button 
            onClick={handleLoadMore} 
            disabled={loading}
            style={{ padding: '12px 32px', background: loading ? '#B0B8C9' : '#00205B', color: loading ? '#fff' : '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontSize: 13, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5 }}
          >
            {loading ? 'Loading...' : `Load More (${total - players.length} remaining)`}
          </button>
        </div>
      )}
    </div>
  );
}

export function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const [tab, setTab] = useState('timeline');
  const [ready, setReady] = useState(false);
  const [shareMsg, setShareMsg] = useState(false);
  const [followingPlayer, setFollowingPlayer] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [player, setPlayer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, hasMore: false, total: 0 });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await playerAPI.getDetails(id);
        setPlayer(res.data.player);
        setMatches(res.data.matches);
        setTournaments(res.data.tournaments);
        setPlayers([res.data.player, ...res.data.relatedPlayers]);
        setPagination(res.data.pagination || { page: 1, hasMore: false, total: res.data.matches.length });
        setReady(true);
      } catch (err) {
        console.error(err);
        setReady(true);
      }
    };
    load();
  }, [id]);

  const loadMoreMatches = async () => {
    if (loadingMore || !pagination.hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const res = await playerAPI.getDetails(id, { page: nextPage });
      setMatches(prev => [...prev, ...res.data.matches]);
      setPlayers(prev => {
        const newPlayers = res.data.relatedPlayers.filter(np => !prev.some(p => p.id === np.id));
        return [...prev, ...newPlayers];
      });
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (currentUser && id) {
      notificationAPI.getFollowed().then(res => {
        setFollowingPlayer((res.data?.players || []).some(pid => String(pid) === String(id)));
      }).catch(() => {});
    }
  }, [currentUser, id]);

  const toggleFollowPlayer = async () => {
    if (!currentUser) return;
    setFollowLoading(true);
    try {
      if (followingPlayer) {
        await notificationAPI.unfollowPlayer(id);
      } else {
        if ('serviceWorker' in navigator && 'PushManager' in window && Notification.permission !== 'denied') {
          try {
            if (Notification.permission === 'default') await Notification.requestPermission();
            if (Notification.permission === 'granted') {
              const reg = await navigator.serviceWorker.ready;
              const { data: { publicKey } } = await notificationAPI.getVapidKey();
              if (publicKey) {
                let sub = await reg.pushManager.getSubscription();
                if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: publicKey });
                await notificationAPI.subscribe(sub.toJSON());
              }
            }
          } catch {}
        }
        await notificationAPI.followPlayer(id);
      }
      setFollowingPlayer(!followingPlayer);
    } catch {}
    setFollowLoading(false);
  };

  if (!ready) return <PlayerDetailSkeleton />;
  if (!player) return <div className="container-sm"><h2>Player not found</h2></div>;
  const pMatches = matches.filter(m => m.player1 && m.player2);
  const wr = player.matchesPlayed ? ((player.wins / player.matchesPlayed) * 100).toFixed(1) : 0;

  return (
    <div className="container-sm">
      <div onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 16, color: '#00205B', fontWeight: 600, fontSize: 14 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back
      </div>
      <Card style={{ padding: 16, position: 'relative' }}>
        <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 6 }}>
          {currentUser && <button onClick={toggleFollowPlayer} disabled={followLoading} style={{ padding: '6px 8px', background: followingPlayer ? 'rgba(30,136,229,0.08)' : 'var(--gray-50)', border: `1px solid ${followingPlayer ? 'rgba(30,136,229,0.3)' : 'var(--card-border)'}`, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill={followingPlayer ? '#1E88E5' : 'none'} stroke={followingPlayer ? '#1E88E5' : 'var(--gray-400)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          </button>}
          <button onClick={() => {
          const url = window.location.href;
          if (navigator.share) navigator.share({ title: player.name, url });
          else { navigator.clipboard.writeText(url); setShareMsg(true); setTimeout(() => setShareMsg(false), 2000); }
          }} style={{ padding: '6px 8px', background: shareMsg ? 'rgba(67,160,71,0.1)' : 'var(--gray-50)', border: '1px solid var(--card-border)', borderRadius: 8, cursor: 'pointer' }}>
            {shareMsg ? <Ico d={Icons.check} size={14} color="#43A047" /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <PlayerAvatar name={player.name} photo={player.photo} size={40} style={{ borderRadius: 10 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="heading-lg" style={{ color: '#00205B', fontSize: 16 }}>{player.name}</h1>
            <div className="sub-text" style={{ fontSize: 12 }}>{player.country}</div>
            {player.bio && <div style={{ fontSize: 12, color: '#8892A6', marginTop: 4, lineHeight: 1.4 }}>{player.bio}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#8892A6', letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" }}>{player.matchesPlayed} MATCHES</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif" }}>{wr}% WIN RATE</span>
        </div>
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#EEF1F6' }}>
          {player.wins > 0 && <div style={{ flex: player.wins, background: '#43A047', transition: 'flex 0.4s' }} />}
          {player.losses > 0 && <div style={{ flex: player.losses, background: '#E53935', transition: 'flex 0.4s' }} />}
          {(player.matchesPlayed - player.wins - player.losses) > 0 && <div style={{ flex: player.matchesPlayed - player.wins - player.losses, background: '#D8DDE8', transition: 'flex 0.4s' }} />}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: '#43A047' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#43A047', fontFamily: "'Outfit', sans-serif" }}>{player.wins}</span>
            <span style={{ fontSize: 10, color: '#8892A6' }}>Won</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: '#E53935' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#E53935', fontFamily: "'Outfit', sans-serif" }}>{player.losses}</span>
            <span style={{ fontSize: 10, color: '#8892A6' }}>Lost</span>
          </div>
          {(player.points || 0) > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto', background: 'rgba(30,136,229,0.08)', padding: '4px 10px', borderRadius: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#1E88E5', fontFamily: "'Outfit', sans-serif" }}>{player.points}</span>
              <span style={{ fontSize: 10, color: '#1E88E5', fontWeight: 600 }}>PTS</span>
            </div>
          )}
        </div>
      </Card>
      {(() => {
        const results = pMatches.filter(m => m.status === 'completed' || m.status === 'live');
        const upcoming = pMatches.filter(m => m.status === 'scheduled');
        const tabs = [
          { key: 'timeline', label: 'Timeline', count: results.length },
          { key: 'upcoming', label: 'Upcoming', count: upcoming.length },
          { key: 'results', label: 'Results', count: results.length },
        ];
        const visible = tab === 'results' ? results : tab === 'upcoming' ? upcoming : results;
        const renderCard = m => {
          const t = tournaments.find(t => t.id === m.tournamentId);
          const isDone = m.status === 'completed';
          const isLive = m.status === 'live';
          const inP1 = m.player1 && (m.player1 === id || (m.player1.includes?.('+') && m.player1.split('+').includes(id)));
          const isWin = isDone && m.winner && (m.winner === id || (m.winner.includes?.('+') && m.winner.split('+').includes(id)));
          const isLoss = isDone && m.winner && !isWin;
          const myScore = k => inP1 ? k.p1 : k.p2;
          const oppScore = k => inP1 ? k.p2 : k.p1;
          const opp = resolveTeam(inP1 ? m.player2 : m.player1, players);
          return (
            <div key={m.id} className="sch-card" style={{ cursor: 'pointer', ...(isLive ? { borderColor: '#E53935' } : {}) }} onClick={() => navigate(`/matches/${m.id}`)}>
              <div className="sch-card-top">
                <span className="sch-card-tourney">{t?.name} · {m.round}</span>
                {isDone ? (
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, padding: '3px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Outfit', sans-serif", ...(isWin ? { background: '#E8F5E9', color: '#43A047' } : { background: '#FFEBEE', color: '#E53935' }) }}>
                    {isWin && <Ico d={Icons.trophy} size={11} color="#43A047" />}
                    {isWin ? 'WIN' : 'LOSS'}
                  </span>
                ) : <StatusBadge status={m.status} />}
              </div>
              <div className="sch-card-players">
                <div className="sch-card-row" style={{ background: isWin ? 'rgba(67,160,71,0.08)' : isLoss ? 'rgba(229,57,53,0.08)' : 'transparent', borderLeft: isWin ? '3px solid #43A047' : isLoss ? '3px solid #E53935' : '3px solid transparent' }}>
                  <PlayerAvatar name={resolveTeam(inP1 ? m.player1 : m.player2, players).name} photo={players.find(p => p.id === (inP1 ? m.player1 : m.player2))?.photo} size={28} />
                  {(() => { const pid2 = inP1 ? m.player1 : m.player2; const isDoubles = pid2?.includes?.('+'); const nameEl = resolveTeam(pid2, players).name; const st = { flex: '1 1 0%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: 12, fontWeight: 700, color: isWin ? '#43A047' : isLoss ? '#E53935' : 'var(--navy)', textDecoration: 'none' }; return isDoubles ? <span style={st}>{nameEl}</span> : <Link to={`/players/${pid2}`} onClick={e => e.stopPropagation()} style={st}>{nameEl}</Link>; })()}
                  {m.score.sets.length > 0 && <div className="sch-card-scores">{m.score.sets.map((s, i) => <span key={i} className={`score-set ${myScore(s) > oppScore(s) ? 'score-set-win' : 'score-set-lose'}`}>{myScore(s)}</span>)}</div>}
                </div>
                <div className="sch-card-row" style={{ borderLeft: '3px solid transparent' }}>
                  <PlayerAvatar name={opp.name} photo={players.find(p => p.id === (inP1 ? m.player2 : m.player1))?.photo} size={28} />
                  {(() => { const oid = inP1 ? m.player2 : m.player1; const isDoubles = oid?.includes?.('+'); const st = { flex: '1 1 0%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: 12, fontWeight: 500, color: 'var(--gray-400)', textDecoration: 'none' }; return isDoubles ? <span style={st}>{opp.name}</span> : <Link to={`/players/${oid}`} onClick={e => e.stopPropagation()} style={st}>{opp.name}</Link>; })()}
                  {m.score.sets.length > 0 && <div className="sch-card-scores">{m.score.sets.map((s, i) => <span key={i} className={`score-set ${oppScore(s) > myScore(s) ? 'score-set-win' : 'score-set-lose'}`}>{oppScore(s)}</span>)}</div>}
                </div>
              </div>
              <div className="sch-card-bottom">
                <span className={`sch-card-time ${isLive ? 'sch-card-time-live' : ''}`}>
                  {isLive ? '● LIVE' : m.time || '—'}
                </span>
                {m.date && <span className="chip-sm"><Ico d={Icons.calendar} size={10} color="#8892A6" />{formatDateIST(m.date, { month: 'short', day: 'numeric' })}</span>}
                {m.court && <span className="sch-card-court">{m.court}</span>}
              </div>
            </div>
          );
        };
        return <>
          <div style={{ display: 'flex', gap: 8, marginTop: 32, marginBottom: 16 }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '6px 16px', borderRadius: 20, border: tab === t.key ? '1.5px solid #00205B' : '1.5px solid #EEF1F6', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif", letterSpacing: 0.5, transition: 'all 0.2s', whiteSpace: 'nowrap', background: tab === t.key ? 'rgba(0,32,91,0.06)' : 'var(--card-bg, #fff)', color: tab === t.key ? '#00205B' : '#8892A6' }}>
                {t.label} ({t.count})
              </button>
            ))}
          </div>
          {tab === 'timeline' ? (() => {
            const completed = results.filter(m => m.status === 'completed' && m.winner);
            const sorted = [...completed].sort((a, b) => {
              const da = a.date || a.updatedAt || '';
              const db = b.date || b.updatedAt || '';
              return da < db ? 1 : da > db ? -1 : 0;
            });
            const streak = sorted.slice(0, 10).map(m => {
              const isWin = m.winner === id || (m.winner?.includes?.('+') && m.winner.split('+').includes(id));
              return isWin ? 'W' : 'L';
            });
            const currentStreak = streak.length > 0 ? (() => {
              const first = streak[0];
              let count = 0;
              for (const r of streak) { if (r === first) count++; else break; }
              return { type: first, count };
            })() : null;

            // Group by tournament
            const grouped = [];
            let lastTid = null;
            sorted.forEach(m => {
              const tid = m.tournamentId;
              if (tid !== lastTid) {
                const t = tournaments.find(t => t.id === tid);
                grouped.push({ tournament: t, matches: [m] });
                lastTid = tid;
              } else {
                grouped[grouped.length - 1].matches.push(m);
              }
            });

            return sorted.length === 0 ? <Card><p style={{ color: '#8892A6' }}>No match history yet</p></Card> : (
              <div>
                {/* Form Streak */}
                <div style={{ background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--card-border)', padding: '14px 16px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--gray-400)', letterSpacing: 1.5, fontFamily: "var(--font-display)" }}>RECENT FORM</span>
                    {currentStreak && (
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 6, fontFamily: "var(--font-display)", letterSpacing: 0.5, background: currentStreak.type === 'W' ? '#E8F5E9' : '#FFEBEE', color: currentStreak.type === 'W' ? '#43A047' : '#E53935' }}>
                        {currentStreak.count} {currentStreak.type === 'W' ? 'WIN' : 'LOSS'} STREAK
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
                    {streak.map((r, i) => (
                      <div key={i} style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, fontFamily: "var(--font-display)", flexShrink: 0, background: r === 'W' ? '#43A047' : '#E53935', color: '#fff', opacity: 1 - (i * 0.03) }}>
                        {r}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    <span style={{ fontSize: 10, color: 'var(--gray-300)' }}>← Most recent</span>
                    <span style={{ fontSize: 10, color: 'var(--gray-300)', marginLeft: 'auto' }}>{streak.filter(r => r === 'W').length}W {streak.filter(r => r === 'L').length}L in last {streak.length}</span>
                  </div>
                </div>

                {/* Timeline */}
                {grouped.map((g, gi) => (
                  <div key={gi} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--navy)', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', fontFamily: "var(--font-display)", letterSpacing: 0.3 }}>{g.tournament?.name || 'Unknown Tournament'}</span>
                    </div>
                    <div style={{ marginLeft: 3, borderLeft: '2px solid var(--gray-100)', paddingLeft: 16 }}>
                      {g.matches.map((m, mi) => {
                        const inP1 = m.player1 && (m.player1 === id || (m.player1.includes?.('+') && m.player1.split('+').includes(id)));
                        const isWin = m.winner && (m.winner === id || (m.winner.includes?.('+') && m.winner.split('+').includes(id)));
                        const opp = resolveTeam(inP1 ? m.player2 : m.player1, players);
                        const myScore = k => inP1 ? k.p1 : k.p2;
                        const oppScore = k => inP1 ? k.p2 : k.p1;
                        const scoreStr = m.score?.sets?.map(s => `${myScore(s)}-${oppScore(s)}`).join(', ') || '';
                        return (
                          <Link to={`/matches/${m.id}`} key={m.id} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, borderRadius: 10, background: 'var(--card-bg)', border: '1px solid var(--card-border)', transition: 'border-color 0.2s', position: 'relative' }}>
                            {/* Timeline dot */}
                            <div style={{ position: 'absolute', left: -25, width: 12, height: 12, borderRadius: '50%', border: '2px solid ' + (isWin ? '#43A047' : '#E53935'), background: 'var(--bg)' }} />
                            {/* Result indicator */}
                            <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: isWin ? '#E8F5E9' : '#FFEBEE' }}>
                              {isWin ? <Ico d={Icons.trophy} size={14} color="#43A047" /> : <span style={{ fontSize: 14, fontWeight: 800, color: '#E53935', fontFamily: "var(--font-display)" }}>L</span>}
                            </div>
                            {/* Match info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: isWin ? '#43A047' : '#E53935', fontFamily: "var(--font-display)" }}>{isWin ? 'W' : 'L'}</span>
                                <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>vs</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "var(--font-display)" }}>{opp.name || 'TBD'}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                <span style={{ fontSize: 10, color: 'var(--gray-300)' }}>{m.round}</span>
                                {m.date && <><span style={{ fontSize: 10, color: 'var(--gray-200)' }}>·</span><span style={{ fontSize: 10, color: 'var(--gray-300)' }}>{formatDateIST(m.date, { month: 'short', day: 'numeric' })}</span></>}
                                {m.specialResult && <><span style={{ fontSize: 10, color: 'var(--gray-200)' }}>·</span><span style={{ fontSize: 10, color: '#E53935', fontWeight: 600 }}>{m.specialResult === 'walkover' ? 'W/O' : m.specialResult === 'retirement' ? 'RET' : 'DQ'}</span></>}
                              </div>
                            </div>
                            {/* Score */}
                            {scoreStr && (
                              <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                                {m.score.sets.map((s, si) => {
                                  const won = myScore(s) > oppScore(s);
                                  return <span key={si} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: won ? 700 : 400, fontFamily: "var(--font-display)", color: won ? 'var(--navy)' : 'var(--gray-300)', background: won ? 'rgba(0,32,91,0.04)' : 'transparent', borderRadius: 4 }}>{myScore(s)}-{oppScore(s)}</span>;
                                })}
                              </div>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : visible.length === 0 ? <Card><p style={{ color: '#8892A6' }}>{tab === 'results' ? 'No results yet' : 'No upcoming matches'}</p></Card>
            : <div className="mh-grid">{visible.map(renderCard)}</div>}
          {pagination.hasMore && tab !== 'upcoming' && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button 
                onClick={loadMoreMatches} 
                disabled={loadingMore}
                style={{ 
                  padding: '10px 24px', 
                  background: loadingMore ? 'var(--gray-100)' : '#00205B', 
                  color: loadingMore ? 'var(--gray-400)' : '#BDFF00', 
                  border: 'none', 
                  borderRadius: 8, 
                  fontWeight: 700, 
                  cursor: loadingMore ? 'not-allowed' : 'pointer', 
                  fontSize: 12, 
                  letterSpacing: 0.5, 
                  fontFamily: "'Outfit', sans-serif",
                  opacity: loadingMore ? 0.6 : 1
                }}
              >
                {loadingMore ? 'Loading...' : `Load More (${pagination.total - matches.length} remaining)`}
              </button>
            </div>
          )}
        </>;
      })()}
    </div>
  );
}
